import { useCallback, useContext, useRef } from 'react'
import { format } from 'date-fns'

import {
  setOrders,
  apiOrdersFetchAllStart,
  apiOrdersFetchAllSuccess,
  apiOrdersFetchAllFailure,
  apiOrdersCreateStart,
  apiOrdersCreateSuccess,
  apiOrdersCreateFailure
} from '../actions/orders'
import { setIsProcessingStripe, setIsStripeError, setStripeError } from '../actions/booking'
import { apiPromoCodeInfoSuccess, setPromoCode, setPromoCodeRefusal } from '../actions/promocodes'
import { promoRefusedAtPay, promoRefusal } from '../../lib/promo-credit'
import { context } from '../context'
import { CONGESTION_ZONE_SERVICE, settings } from '../constants'
import { transformOrderKeys } from '../adapters'
import { actions, AnalyticsService, categories } from '../../services/analytics'
import { useSelectors } from './selectors/use-selectors'
import { ANALYTICS_STEPS, STEPS } from '../../pages/booking/components/steps'
import { saveBookingState } from '../booking-persistence'
import { stripeErrorDetails } from '../../lib/stripe-load'
import { addressWithPostcode } from '../../lib/postcode'
import { adClickParams } from '../../lib/ad-click'
// The booking-flow experiment was retired on 2026-10-04, so the order no
// longer carries ab_variant (the arm it carried was the wiped-flag fallback,
// not the arm the customer saw - client/gwmc/lib/booking-flow.js).
// PostHog person + session-replay ids, so the server-side order_paid /
// order_completed can be attached to this exact session (ANALYTICS.md §9).
// posthog-js is loaded by the layout only when POSTHOG_KEY is set.
const posthogId = getter => {
  try {
    const ph = typeof window !== 'undefined' ? window.posthog : null
    const v = ph && typeof ph[getter] === 'function' ? ph[getter]() : ''
    return typeof v === 'string' ? v : ''
  } catch (e) { return '' }
}

export const useOrdersApi = () => {
  const { state, dispatch } = useContext(context)

  const {
    agentTip,
    address,
    postcode,
    date,
    timeSlot,
    homeAddress,
    carModel,
    carColor,
    carRegistration,
    notes,
    selectedServices,
    selectedServiceIds,
    promoCode,
    promoCodeInfo
  } = useSelectors()

  // handleCheckout runs after awaits (guest sign-up), by which time the
  // closure it was created in can be a render behind the store. It reads the
  // booking through this ref, which always holds the latest values.
  const latest = useRef({})
  latest.current = { agentTip, address, postcode, date, timeSlot, homeAddress, carModel, carColor, carRegistration, notes, selectedServiceIds, promoCode, promoCodeInfo, state }

  const apiOrderFetchAll = useCallback(() => {
    const token = window.localStorage.getItem('token')

    if (!token) return

    const options = {
      headers: { 'X-User-Token': token }
    }

    dispatch(apiOrdersFetchAllStart())
    fetch(`${settings.host}/api/v2/orders`, options)
      .then(response => response.json())
      .then((json = {}) => {
        const { status, data: orders } = json

        if (status !== 'ok') throw new Error(`json: ${JSON.stringify(json)}`)

        dispatch(apiOrdersFetchAllSuccess())
        dispatch(setOrders(transformOrderKeys(orders)))
      })
      .catch(error => {
        console.error('[apiOrderFetchAll] error:', error)
        dispatch(apiOrdersFetchAllFailure(`apiOrderFetchAll error: ${error?.message || error}`))
      })
  }, [dispatch])

  // The two typed fields become one `address` - the string the operator's
  // "show in Google Maps" link is built from - plus `postcode` on its own
  // column, which is what the area pages and the dispatch note read. No
  // lat/lng: nothing geocodes any more, so there are no coordinates to send
  // and nothing can disagree with the text the customer typed. A postcode
  // the customer also typed into the address is not appended a second time.
  const fullAddress = order => addressWithPostcode(order.address, order.postcode)

  const prepareOrderParams = useCallback(
    order => {
      return JSON.stringify({
        agent_tip: order.agentTip,
        date: format(order.date, 'dd-MM-yyyy'),
        address: fullAddress(order),
        postcode: order.postcode,
        home_address: order.homeAddress,
        make_model: order.carModel,
        color: order.carColor,
        registration: order.carRegistration,
        notes: order.notes,
        time_bucket: order.timeSlot,
        // Which answers the FORM chose, not the customer (31 Oct 2026). They
        // land on orders.date_assumed / orders.car_size_assumed, which is
        // what Order#missing_details reports and Aider confirms. The server
        // also stamps them when IT fills a blank, so a client that sends
        // neither still produces an honest order.
        date_assumed: !!order.assumed?.date,
        car_size_assumed: !!order.assumed?.carSize,
        service_ids: order.selectedServiceIds,
        // A credit or referral code is held as promoCodeInfo (checked, not yet
        // applied: it needs the basket), so it rides the order too.
        promo_code: order.promoCode?.code || latest.current.promoCodeInfo?.code,
        // PostHog ids for server-side order_paid attribution (§ above)
        posthog_distinct_id: posthogId('get_distinct_id'),
        posthog_session_id: posthogId('get_session_id'),
        // Google Ads click id + utm_* from the landing URL, for the
        // Paid booking conversion feed (docs/GOOGLE_ADS.md)
        ...adClickParams()
      });
    }
  )

  const apiOrderCreate = useCallback(
    order => {
      const options = {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-User-Token': window.localStorage.getItem('token')
        },
        body: prepareOrderParams(order)
      }

      dispatch(apiOrdersCreateStart())

      return fetch(`${settings.host}/api/v2/orders`, options)
        .then(response => response.json())
        .then((json = {}) => {
          const { status } = json

          if (status !== 'ok') throw new Error(`json: ${JSON.stringify(json)}`)

          AnalyticsService.trackEvent(categories.booking, actions.orderPlaced, 'GWMC Fee', json.data?.net_gwmc_fee)
          AnalyticsService.trackPurchase(json.data, selectedServices, promoCode?.code)
          AnalyticsService.trackCheckoutStep(ANALYTICS_STEPS[STEPS.FINISH])

          dispatch(apiOrdersCreateSuccess())

          return json.data
        })
        .catch(error => {
          // Error instances stringify to "{}", losing message + stack - log
          // them explicitly so we get something useful in browser devtools
          // and any forwarded console capture.
          console.error('[apiOrderCreate] error:', error)
          dispatch(apiOrdersCreateFailure(`apiOrderCreate error: ${error?.message || error}`))

          return null
        })
    },
    [dispatch, promoCode, selectedServices]
  )

  // Finalize a paid draft and answer the order for the success page. Two
  // routes reach it: process_checkout (hosted Checkout, by session id) and
  // process_embedded_checkout (the one embedded checkout, by SetupIntent id).
  const finalizeOrder = useCallback(
    (path, body, label) => {
      const options = {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-User-Token': window.localStorage.getItem('token')
        },
        body: JSON.stringify(body)
      }

      dispatch(apiOrdersCreateStart())

      return fetch(`${settings.host}/api/v2/orders/${path}`, options)
        .then(response => response.json())
        .then((json = {}) => {
          const { status } = json

          if (status !== 'ok') throw new Error(`json: ${JSON.stringify(json)}`)


          AnalyticsService.trackEvent(categories.booking, actions.orderPlaced, 'GWMC Fee', json.data?.net_gwmc_fee)
          AnalyticsService.trackPurchase(json?.data, json.data?.track_services, json.data?.promo_code)
          AnalyticsService.trackCheckoutStep(ANALYTICS_STEPS[STEPS.FINISH])

          dispatch(apiOrdersCreateSuccess())

          return json.data
        })
        .catch(error => {
          console.error(`[${label}] error:`, error)
          dispatch(apiOrdersCreateFailure(`${label} error: ${error?.message || error}`))

          return null
        })
    },
    [dispatch]
  )

  const handleProcessCheckout = useCallback(
    (stripe, sessionId) => finalizeOrder('process_checkout', { session_id: sessionId }, 'handleProcessCheckout'),
    [finalizeOrder]
  )

  const handleProcessEmbeddedCheckout = useCallback(
    setupIntentId => finalizeOrder('process_embedded_checkout', { setup_intent_id: setupIntentId }, 'handleProcessEmbeddedCheckout'),
    [finalizeOrder]
  )

  const handleCheckout = useCallback(
    (stripe) => {
      const { agentTip, address, postcode, date, assumed, timeSlot, homeAddress, carModel, carColor, carRegistration, notes, selectedServiceIds, promoCode, state } = latest.current
      try {
        AnalyticsService.trackEvent(categories.booking, actions.clickPlaceOrder)
        AnalyticsService.trackCheckoutStep(ANALYTICS_STEPS[STEPS.CHECKOUT])
      } catch (e) {
        console.warn('[Analytics] trackCheckoutStep error:', e)
      }

      // NO ADDRESS IS NOT A REFUSAL ANY MORE (Robin, 31 Oct 2026: a dumb
      // form; lib/pay-defaults.js). This guard used to require an address as
      // well, which made it the last place on the pay path that could still
      // say no to a customer who was willing to pay. An order with nowhere to
      // drive to is taken now, held off the operator feed
      // (Order::DetailsHold) and completed by Aider.
      //
      // What is left is not a validation, it is the two things
      // `prepareOrderParams` cannot build a request without: a real date (it
      // formats one, and `format(null)` throws RangeError) and at least one
      // priced service (a £0 order must never be posted). The booking form
      // always carries both, the day because it fills it in and SHOWS it
      // (date_assumed), so this is the refresh-nuked-redux backstop it was
      // written as and not a field check.
      const dateInstance = date instanceof Date ? date : (date ? new Date(date) : null)
      if (!dateInstance || isNaN(dateInstance.getTime()) || !selectedServiceIds?.length) {
        dispatch(setStripeError('Your booking details are missing - please go back and re-enter them.'))
        dispatch(setIsStripeError(true))
        return
      }

      const order = {
        agentTip,
        address,
        postcode,
        date: dateInstance,
        assumed,
        timeSlot,
        homeAddress,
        carModel,
        carColor,
        carRegistration,
        notes,
        selectedServiceIds,
        promoCode
      }

      dispatch(setIsProcessingStripe(true))

      // GA4 - the user has committed to the card path. Wallet has its
      // own pair (payment_method_selected('wallet') + wallet_payment_*)
      // so this fires exclusively for the hosted-redirect flow.
      AnalyticsService.trackPaymentMethodSelected('card')
      AnalyticsService.trackCardPaymentStarted()

      fetch(`${settings.host}/api/v2/orders/checkout`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'X-User-Token': window.localStorage.getItem('token') },
        body: prepareOrderParams(order)
      })
        .then(response => response.json())
        .then(json => {
          if (json.status === 'ok') {
            // Persist booking state so it survives the Stripe redirect
            saveBookingState(state)
            // No Stripe.js (it failed to load, lib/stripe.js): the session's
            // own URL is Stripe's hosted page, reachable without it.
            if (!stripe) {
              if (json.data?.session_url) {
                window.location.assign(json.data.session_url)
                return
              }
              dispatch(setIsProcessingStripe(false))
              dispatch(setStripeError('Payments could not load on this connection. Please check your signal and try again.'))
              dispatch(setIsStripeError(true))
              AnalyticsService.trackStripeError('stripe_js_unavailable')
              return
            }
            return stripe.redirectToCheckout({sessionId: json.data?.session_id})
              .then(result => {
                if (result?.error) {
                  dispatch(setIsProcessingStripe(false))
                  dispatch(setStripeError(result.error.message))
                  dispatch(setIsStripeError(true))
                  AnalyticsService.trackStripeError('redirect', result.error.message, stripeErrorDetails(result.error))
                }
              })
          } else {
            // The code was refused for who is booking: drop it, so the total
            // shown is what the card pays (lib/promo-credit.js).
            if (promoRefusedAtPay(json)) {
              AnalyticsService.trackPromoCodeRejected(json.data, 'pay_press')
              const heldCode = latest.current.promoCode?.code || latest.current.promoCodeInfo?.code
              dispatch(setPromoCode(null))
              dispatch(apiPromoCodeInfoSuccess(null))
              dispatch(setPromoCodeRefusal(promoRefusal(json, heldCode)))
            }
            dispatch(setIsProcessingStripe(false))
            dispatch(setStripeError(json?.message || 'Unable to create checkout session'))
            dispatch(setIsStripeError(true))
            // The server's postcodes.io check (Order#postcode_exists) refused
            // the postcode: the same field_blocked the form's own checks send.
            if (/postcode/i.test(json?.message || '')) AnalyticsService.trackFieldBlocked('book', 'postcode', 'postcode_not_found')
            AnalyticsService.trackStripeError('session_create', json?.message)
          }
        })
        .catch(error => {
          dispatch(setIsProcessingStripe(false))
          dispatch(setStripeError(error?.message || 'Something went wrong. Please try again.'))
          dispatch(setIsStripeError(true))
          AnalyticsService.trackStripeError('network', error?.message)
        })
    },
    [apiOrderCreate, dispatch]
  )

  // In-app wallet (Apple Pay / Google Pay / Link). Returns the SetupIntent
  // client_secret + draft order id; the caller confirms with Stripe.js using a
  // PaymentRequest paymentMethod, then calls handleProcessExpressCheckout to
  // finalize.
  //
  // The address and postcode are the ones the customer typed, exactly as on
  // the card path: the wallet only pays and is never asked for an address.
  // The booking is read through `latest`, for the same reason handleCheckout
  // does: this runs straight after the wallet sheet closes, and a closure
  // over the store could be a render behind.
  const apiCreateExpressCheckout = useCallback(() => {
    const { agentTip, address, postcode, date, assumed, timeSlot, homeAddress, carModel, carColor, carRegistration, notes, selectedServiceIds, promoCode } = latest.current
    const order = {
      agentTip,
      address,
      postcode,
      date,
      assumed,
      timeSlot,
      homeAddress,
      carModel,
      carColor,
      carRegistration,
      notes,
      selectedServiceIds,
      promoCode
    }

    return fetch(`${settings.host}/api/v2/orders/express_checkout`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'X-User-Token': window.localStorage.getItem('token') },
      body: prepareOrderParams(order)
    }).then(response => response.json())
  }, [prepareOrderParams])

  // EMBEDDED_CHECKOUT: the draft and the SetupIntent for the one embedded
  // checkout, created after the payer has chosen (wallet sheet approved, or
  // the card form submitted) and before Stripe.js confirms. Same typed
  // address and postcode as every other path, read through `latest`.
  // checkoutKey: this tab's attempt (lib/payer-email.js). A retry with the
  // same booking gets the same draft and SetupIntent back.
  // wallet: a wallet payer's sheet (lib/wallet-address.js, 18 Oct 2026):
  // { address, postcode, zone } where the car is and whether the Congestion
  // Charge rate was chosen (null: the page's own tick stands). It replaces
  // the typed address, postcode and tick; nothing else changes.
  const apiCreateEmbeddedCheckout = useCallback((payerEmail, checkoutKey, wallet = null, confirmationToken = null) => {
    const { agentTip, date, assumed, timeSlot, homeAddress, carModel, carColor, carRegistration, notes, promoCode } = latest.current
    const address = wallet ? wallet.address : latest.current.address
    const postcode = wallet ? wallet.postcode : latest.current.postcode
    const zoneId = CONGESTION_ZONE_SERVICE.id
    let selectedServiceIds = latest.current.selectedServiceIds
    if (wallet && wallet.zone != null && zoneId) {
      selectedServiceIds = selectedServiceIds.filter(id => id != zoneId).concat(wallet.zone ? [zoneId] : []) // eslint-disable-line eqeqeq
    }
    // `!address` is gone from this guard (31 Oct 2026): a booking with
    // nowhere to drive to is taken, not refused. See handleCheckout above for
    // why the date and the services are not field checks.
    const dateInstance = date instanceof Date ? date : (date ? new Date(date) : null)
    if (!dateInstance || isNaN(dateInstance.getTime()) || !selectedServiceIds?.length) {
      return Promise.resolve({ status: 'error', message: 'Your booking details are missing - please go back and re-enter them.' })
    }
    const order = {
      agentTip,
      address,
      postcode,
      date: dateInstance,
      assumed,
      timeSlot,
      homeAddress,
      carModel,
      carColor,
      carRegistration,
      notes,
      selectedServiceIds,
      promoCode
    }
    // No redirect mark here (a booking-state save without a form makes one):
    // a card confirm in the page is not a redirect, and the mark made every
    // later reload of Book & pay count as a return from Stripe
    // (stripe_checkout_returned at 12:41 on 12 Oct 2026 was a Safari tab
    // reload). Book & pay saves the form on every change (use-book-draft.js).
    const body = JSON.parse(prepareOrderParams(order))
    if (payerEmail) body.payer_email = payerEmail
    if (checkoutKey) body.checkout_key = checkoutKey
    // The card about to be saved, so a referral code paid with the referrer's
    // own card is refused before any money moves (Referral::Identity).
    if (confirmationToken) body.confirmation_token = confirmationToken
    return fetch(`${settings.host}/api/v2/orders/embedded_checkout`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'X-User-Token': window.localStorage.getItem('token') },
      body: JSON.stringify(body)
    }).then(response => response.json())
  }, [prepareOrderParams])

  // PAY_BY_LINK (1 Nov 2026): book without paying. The same booking body as
  // the card path's draft, plus the typed email the Checkout link goes to,
  // and no Stripe at all in the browser. The server creates the order pending
  // payment, holds it off the operator feed and emails the link
  // (Order::PayByLink); nothing of the new-order pipeline runs here.
  const apiBookWithoutPayment = useCallback(payerEmail => {
    const { agentTip, date, assumed, timeSlot, homeAddress, carModel, carColor, carRegistration, notes, promoCode } = latest.current
    const { address, postcode, selectedServiceIds } = latest.current
    // The same two guards the card path keeps, and for the same reason: an
    // order has to have a day and a price. Neither is a field check on the
    // customer (a blank day is defaulted server side), they are the shape the
    // request needs to be answerable at all.
    const dateInstance = date instanceof Date ? date : (date ? new Date(date) : null)
    if (!dateInstance || isNaN(dateInstance.getTime()) || !selectedServiceIds?.length) {
      return Promise.resolve({ status: 'error', message: 'Your booking details are missing - please go back and re-enter them.' })
    }
    const body = JSON.parse(prepareOrderParams({
      agentTip,
      address,
      postcode,
      date: dateInstance,
      assumed,
      timeSlot,
      homeAddress,
      carModel,
      carColor,
      carRegistration,
      notes,
      selectedServiceIds,
      promoCode
    }))
    if (payerEmail) body.payer_email = payerEmail
    return fetch(`${settings.host}/api/v2/orders/pay_by_link`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'X-User-Token': window.localStorage.getItem('token') },
      body: JSON.stringify(body)
    }).then(response => response.json())
  }, [prepareOrderParams])

  // The wallet sheet's address while the sheet is open (shippingaddresschange,
  // lib/wallet-address.js): its country and redacted postcode only, with the
  // booking as the page has it, for coverage and the price there
  // (Order::WalletAddressCheck). The answer's data, or null when it could not
  // be asked; the caller then goes on at the page's price.
  const apiWalletAddress = useCallback(sheetAddress => Promise.resolve()
    .then(() => {
      const l = latest.current
      return fetch(`${settings.host}/api/v2/orders/wallet_address`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'X-User-Token': window.localStorage.getItem('token') },
        body: JSON.stringify({
          ...sheetAddress,
          date: format(new Date(l.date), 'dd-MM-yyyy'),
          time_bucket: l.timeSlot,
          service_ids: l.selectedServiceIds,
          promo_code: l.promoCode?.code || l.promoCodeInfo?.code
        })
      })
    })
    .then(response => response.json())
    .then(json => (json?.status === 'ok' ? json.data : null))
    .catch(() => null), [])

  const handleProcessExpressCheckout = useCallback(
    setupIntentId => {
      return fetch(`${settings.host}/api/v2/orders/process_express_checkout`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'X-User-Token': window.localStorage.getItem('token') },
        body: JSON.stringify({ setup_intent_id: setupIntentId })
      }).then(response => response.json())
    },
    []
  )

  return {
    apiOrderFetchAll,
    apiOrderCreate,
    handleCheckout,
    handleProcessCheckout,
    apiCreateExpressCheckout,
    handleProcessExpressCheckout,
    apiCreateEmbeddedCheckout,
    apiBookWithoutPayment,
    apiWalletAddress,
    handleProcessEmbeddedCheckout
  }
}
